// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CustomEvent } from '@shared/utils/CustomEvents';
import { CustomErrorClass, ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { ToolsCache } from '@shared/commons/cache/ToolsCache';
import { popupErrorHandler, workspaceManagerHandler } from '@shared/utils/AppHandlers';
import { UserWebStorageInstance } from '../../user-web-storage';
import { DockSystemInstance } from '../DockSystem';
import { TerceraPopupBaloon } from '../elements/TerceraPopupBaloon';
import { WorkSpace } from './WorkSpace';
import { WorkSpaceDBManager } from './WorkSpaceDBManager';
import { DataCache } from '@shared/commons/DataCache';
import { TerceraSymbolLookupBaseDataProvider } from '@shared/commons/NoNFixedListCore';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { BrowserUtils } from '@shared/commons/UtilsClasses/BrowserUtils';
import { ApplicationInfo } from '@shared/commons/ApplicationInfo';
import { DataBaseActions } from '@shared/commons/DBClasses/DBPrimitives';
import { WorkSpaceMetaData } from './WorkSpaceMetaData';
import { wmh } from '@shared/commons/UtilsClasses/WorkspaceManagerHelper';
import { WDSettings } from '../settings/WDGeneralSettings';
import { ApplicationVersion } from '@shared/commons/ApplicationVersion';
export const MAX_WORKSPACE_COUNT = 10;
export const FILENAME_PREFIX = 'WorkSpace';
class _WorkSpaceManager {
    constructor() {
        this.wsList = null;
        this.synchronizeWorkspacesBeforeExit = () => __awaiter(this, void 0, void 0, function* () {
            this.stopSaveLoop();
            yield this.saveWorkSpace();
            this.clearCurrentWorkSpace();
            yield this.synchronizeWorkspaces();
        });
        this.currentWorkspace = null;
        this.wsList = {}; // <id>:<name>
        this.workSpaceListChanged = new CustomEvent();
        this.needRestoreWS = false; // при авторелогине
        this.WorkspacesLimitReached = new CustomEvent();
        WDSettings.settingsChanged.Subscribe(this.updateSettings, this);
        this.saveIntervalId = null;
        this.LoadedDataWorkSpace = new LoadWorkSpaceResponse();
        this.OnWorkSpaceChanged = new CustomEvent();
        wmh.__init(this.saveWorkSpace.bind(this), this.synchronizeWorkspaces.bind(this));
    }
    initWorkspaces() {
        return __awaiter(this, void 0, void 0, function* () {
            const IdsFromDB = yield WorkSpaceDBManager.GetAllWorkSpacesIds();
            this.wsList = {}; // clear prew
            return yield UserWebStorageInstance.workspaces
                .getInitData()
                .then((data) => __awaiter(this, void 0, void 0, function* () {
                // TODO. Ugly. Part 1.
                let activeWsId = data.active_ws_id;
                const wsDataArray = data.ws_data_array;
                WorkSpaceManager.clearCurrentWorkSpace();
                const [newWsDataArray, isNewActiveWsId, newWSidFromDb] = yield WorkSpaceDBManager.FilterWorkSpacesIds(wsDataArray, activeWsId);
                if (!isNullOrUndefined(newWSidFromDb)) {
                    activeWsId = newWSidFromDb;
                }
                const [mergedWS, newWSidFrommerge] = this.mergeWsArrays(newWsDataArray, IdsFromDB);
                if (!isNullOrUndefined(newWSidFrommerge)) {
                    activeWsId = newWSidFrommerge;
                }
                let listToPopulate = mergedWS;
                for (const wObj of mergedWS) {
                    if (!wObj.name) {
                        listToPopulate = wsDataArray.map((ws) => {
                            const wmd = new WorkSpaceMetaData();
                            wmd.FillByDataFromServer(ws, false);
                            return wmd;
                        });
                        yield WorkSpaceDBManager.ClearAll();
                        break;
                    }
                }
                this.addArrayToWsList(listToPopulate);
                if (isNewActiveWsId) {
                    activeWsId = null;
                }
                const activeWsLoadingHandler = this.processInitWorkSpacesList(listToPopulate, activeWsId);
                return [true, activeWsId, activeWsLoadingHandler];
            }))
                .catch(() => {
                const ex = new CustomErrorClass('WorkSpaceManager error', 'WorkSpaceManager.initWorkspaces', 'initWorkspaces -> getInitData');
                ErrorInformationStorage.GetException(ex);
                this.addArrayToWsList(IdsFromDB);
                // this.LoadedDataWorkSpace = new LoadWorkSpaceResponse();
                // this.createWorkSpace();
                return [false, '', null];
            });
        });
    }
    mergeWsArrays(fromServer, fromDB) {
        const tmpMap = {};
        const resArray = [];
        let serverActive = null;
        let newActiveId = null;
        for (const item of fromServer) {
            if (isNullOrUndefined(tmpMap[item.ws_id])) {
                tmpMap[item.ws_id] = item;
                item.fromServer = true;
                item.needLoad = true;
                if (item.isActive) {
                    serverActive = item;
                    newActiveId = item.ws_id;
                }
                resArray.push(item);
            }
        }
        for (const item of fromDB) {
            if (isNullOrUndefined(tmpMap[item.ws_id])) {
                tmpMap[item.ws_id] = item;
                item.fromServer = false;
                item.needLoad = false;
                resArray.push(item);
            }
            else {
                const tmp = tmpMap[item.ws_id];
                tmp.needLoad = tmp.updateTimeSpan > item.updateTimeSpan;
            }
            if (item.isActive) {
                if (serverActive === null) {
                    newActiveId = item.ws_id;
                }
                else if (item.updateTimeSpan >= serverActive.updateTimeSpan) {
                    newActiveId = item.ws_id;
                }
            }
        }
        return [resArray, newActiveId];
    }
    processInitWorkSpacesList(wsDataArray, activeWsId) {
        return __awaiter(this, void 0, void 0, function* () {
            let ActiveWsPrommise = null;
            for (const wsItem of wsDataArray) {
                if (!wsItem.needLoad) {
                    continue;
                }
                const wsId = wsItem.ws_id;
                const tmpHandler = this.loadWSFromServerAndSaveInDB(wsId);
                if (activeWsId === wsId) {
                    ActiveWsPrommise = tmpHandler;
                }
            }
            return ActiveWsPrommise;
        });
    }
    loadWSFromServerAndSaveInDB(wsId) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield UserWebStorageInstance.workspaces
                .get(wsId)
                .then((wsData) => __awaiter(this, void 0, void 0, function* () {
                yield WorkSpaceDBManager.SaveWorkSpace(wsId, wsData, true);
            }))
                .catch(() => __awaiter(this, void 0, void 0, function* () {
                return yield WorkSpaceDBManager.GetWorkSpace(wsId);
            }));
        });
    }
    saveWorkSpace() {
        return __awaiter(this, void 0, void 0, function* () {
            const ws = this.currentWorkspace;
            if (ws === null) {
                yield Promise.resolve();
                return;
            } // Promise.reject('currentWorkspace doesn\'t exist.');
            const reqObj = this.getWsDataForSave(ws);
            if (reqObj !== null) {
                yield this.saveWorkSpaceToIndexedDb(ws.fileName, reqObj);
            }
            else {
                yield Promise.resolve();
            }
        });
    }
    getWsDataForSave(ws) {
        if (ws === null)
            return null;
        const tools = ToolsCache.GetToolsForSave();
        const reqObj = {
            data: ws.serializePanels(),
            name: ws.visibleName,
            locked: ws.locked,
            panels_amount: ws.getPanelsAmount(),
            tools,
            app_version: ApplicationInfo.BuildVersionData.serialize()
        };
        return reqObj;
    }
    saveWorkSpaceToIndexedDb(name, reqObj) {
        return __awaiter(this, void 0, void 0, function* () {
            if (ApplicationInfo.isExploreMode) {
                yield Promise.resolve();
                return;
            }
            yield WorkSpaceDBManager.SaveWorkSpace(name, reqObj);
            if (wmh.isShowLogs()) {
                console.warn(`Workspace ${name} was saved to IndexedDB`);
            }
        });
    }
    setActiveWorkspaceId(wsID) {
        return __awaiter(this, void 0, void 0, function* () {
            yield WorkSpaceDBManager.SetActiveWorkSpace(wsID);
        });
    }
    restoreWorkSpace(wsData) {
        var _a;
        const currentWorkspace = this.currentWorkspace;
        if (currentWorkspace === null)
            return;
        try {
            const wsAppVersion = ApplicationVersion.createApplicationVersionByJson(wsData.app_version);
            const isPropertiesFromOldVersion = (_a = wsAppVersion === null || wsAppVersion === void 0 ? void 0 : wsAppVersion.isPartialOlderThan(ApplicationInfo.BuildVersionData)) !== null && _a !== void 0 ? _a : true;
            ToolsCache.CreateToolsFromSaves(wsData.tools, DataCache, isPropertiesFromOldVersion);
            currentWorkspace.visibleName = wsData.name;
            currentWorkspace.updateLocked(wsData.locked);
            currentWorkspace.loadPanels(wsData.data);
            currentWorkspace.populate();
        }
        catch (e // #99183
        ) {
            try {
                const brokenWorkspaceID = currentWorkspace.fileName;
                this.removeWorkSpaces([brokenWorkspaceID]);
                this.deleteFromWsList(brokenWorkspaceID);
                const ex = new CustomErrorClass('WorkSpaceManager', 'restoreWorkSpace', 'restoreWorkSpace -> loadPanels');
                ErrorInformationStorage.GetException(ex);
                currentWorkspace.allPanels = {};
                DockSystemInstance.clearDocking();
            }
            finally {
                UserWebStorageInstance.workspaces
                    .getDefaultData()
                    .then((defaultData) => {
                    if (defaultData) {
                        const newActiveID = defaultData.active_ws_id;
                        if (newActiveID) {
                            void this.changeWorkSpace(newActiveID, true);
                        }
                    }
                })
                    .catch((e) => {
                    this.getNewCurrentWs(); // на случай если дефолтового нет
                });
            }
        }
        DockSystemInstance.dockManager.hideCloseButton(currentWorkspace.locked);
        this.workSpaceListChanged.Raise();
    }
    RestoreWorkspaces() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.needRestoreWS) {
                yield Promise.resolve();
                return;
            }
            if (this.LoadedDataWorkSpace.IsLoaded) {
                if (this.LoadedDataWorkSpace.JsonData) {
                    this.restoreWorkSpace(this.LoadedDataWorkSpace.JsonData);
                }
                yield Promise.resolve();
                return;
            }
            yield new Promise((resolve, reject) => {
                this.LoadedDataWorkSpace.OnLoadedDelegate = resolve;
            });
        });
    }
    // TODO. Refactor.
    changeWorkSpace(wsId_1) {
        return __awaiter(this, arguments, void 0, function* (wsId, noNeedToSaveOldWS = false, isLoadIfNeeded = false) {
            var _a;
            if (!noNeedToSaveOldWS) {
                yield this.saveWorkSpace();
            }
            this.disposeCurrentWorkspaceObj();
            yield this.setActiveWorkspaceId(wsId);
            let ws = yield WorkSpaceDBManager.GetWorkSpace(wsId);
            if (isNullOrUndefined(ws) && isLoadIfNeeded) {
                yield this.loadWSFromServerAndSaveInDB(wsId);
                ws = yield WorkSpaceDBManager.GetWorkSpace(wsId);
            }
            this.createCurrentWorkspaceObj(wsId, this.wsList[wsId]);
            if ((_a = ws === null || ws === void 0 ? void 0 : ws.data) === null || _a === void 0 ? void 0 : _a.insDict) {
                const instrs = ws.data.insDict;
                const k = Object.keys(instrs);
                if (k.length !== 0) {
                    const insReqArr = [];
                    for (let i = 0; i < k.length; i++) {
                        const ins = k[i];
                        const DataProvider = new TerceraSymbolLookupBaseDataProvider();
                        if (ins && !DataCache.getInstrumentByName(ins)) {
                            insReqArr.push(DataProvider.getInstrumentByName(ins));
                        }
                    }
                    // TODO костыль исправить с тикетом 84863
                    DataCache.currentWorkspaceInsDict = instrs || {};
                    if (insReqArr.length > 0) {
                        yield Promise.all(insReqArr);
                    }
                }
            }
            if (ws === null) {
                this.getNewCurrentWs();
            }
            else if (this.LoadedDataWorkSpace.OnLoadedDelegate) {
                this.restoreWorkSpace(ws);
                this.LoadedDataWorkSpace.OnLoadedDelegate();
                this.LoadedDataWorkSpace.Clear();
            }
            else if (MainWindowManager.IsWorkMode) {
                this.restoreWorkSpace(ws);
            }
            else {
                this.LoadedDataWorkSpace.IsLoaded = true;
                this.LoadedDataWorkSpace.JsonData = ws;
            }
            this.OnWorkSpaceChanged.Raise();
        });
    }
    // -------------------------------- Shieeeeet ---------------------------------//
    closeWorkSpace(wsId) {
        if (wsId in this.wsList) {
            this.closeWorkspacesWork([wsId]);
        }
    }
    closeAllWorkSpacesButCurrent() {
        const curWsId = this.currentWorkspace.fileName;
        const wsList = this.wsList;
        const ids = [];
        for (const wsId in wsList) {
            if (wsId !== curWsId) {
                ids.push(wsId);
            }
        }
        this.closeWorkspacesWork(ids);
    }
    closeWorkspacesWork(ids) {
        if (isNullOrUndefined(ids) && ids.length === 0) {
            return;
        }
        for (const id of ids) {
            this.deleteFromWsList(id);
        }
        this.removeWorkSpaces(ids);
        // TODO. Ugly.
        const curWs = this.currentWorkspace;
        const curWsId = curWs !== null ? curWs.fileName : null;
        // Current workspace is still here - do nothing.
        if (curWsId in this.wsList) {
            return;
        }
        this.disposeCurrentWorkspaceObj();
        this.getNewCurrentWs();
    }
    removeWorkSpaces(ids) {
        for (const id of ids) {
            void WorkSpaceDBManager.MarkToRemoveWorkSpace(id);
        }
    }
    // -------------------------------- Shieeeeet ---------------------------------//
    AddWorkSpace() {
        void this.saveWorkSpace();
        this.createWorkSpace();
    }
    createWorkSpace() {
        if (this.isWorkspaceLimitReached()) {
            return;
        }
        const wsId = this.getNewWorkSpaceFileName();
        const wsName = this.getDefaultWorkSpaceName();
        WorkSpaceDBManager.SaveWorkSpace(wsId, { name: wsName })
            .then(() => {
            void this.setActiveWorkspaceId(wsId);
        })
            .catch(() => { });
        this.LoadedDataWorkSpace.IsLoaded = true;
        this.addToWsList(wsId, wsName);
        this.disposeCurrentWorkspaceObj();
        this.createCurrentWorkspaceObj(wsId, this.wsList[wsId]);
        this.OnWorkSpaceChanged.Raise();
    }
    cloneCurrentWorkSpace() {
        if (this.isWorkspaceLimitReached()) {
            return;
        }
        const wsList = this.wsList;
        const keys = Object.keys(wsList);
        let clonedWsNameNew = '';
        const dictToFind = {};
        const clonedWsId = this.getNewWorkSpaceFileName();
        const clonedWsName = this.currentWorkspace.visibleName + '-';
        for (let i = 0; i < keys.length; i++) {
            dictToFind[wsList[keys[i]]] = keys[i];
        }
        for (let i = 1; i <= MAX_WORKSPACE_COUNT; i++) {
            clonedWsNameNew = clonedWsName + i;
            if (!dictToFind[clonedWsNameNew])
                break;
        }
        void WorkSpaceDBManager.CloneWorkSpace(this.currentWorkspace.fileName, clonedWsId).then(() => __awaiter(this, void 0, void 0, function* () {
            this.addToWsList(clonedWsId, clonedWsNameNew);
            yield this.changeWorkSpace(clonedWsId);
            yield this.tryChangeWorkSpaceVisibleName(clonedWsId, clonedWsNameNew);
        }));
    }
    // -------------------------------- Shieeeeet ---------------------------------//
    checkText(tb, newName) {
        if (BrowserUtils.regexpSymbol.test(newName)) {
            popupErrorHandler.Show(tb, Resources.getResource('textBoxWithValidating.ErrorText.NameContainsInvalidChar'), Resources.getResource('screen.error.title'), true, true);
            return true;
        }
        if (BrowserUtils.regexpEnd.test(newName) || BrowserUtils.regexpText.test(newName)) {
            popupErrorHandler.Show(tb, Resources.getResource('textBoxWithValidating.ErrorText.NameExist'), Resources.getResource('screen.error.title'), true, true);
            return true;
        }
        popupErrorHandler.Hide(tb);
        return false;
    }
    tryChangeWorkSpaceVisibleName(wsFileName, newName) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(wsFileName in this.wsList)) {
                return false;
            }
            this.wsList[wsFileName] = newName;
            if (this.currentWorkspace.fileName === wsFileName) {
                this.currentWorkspace.visibleName = newName;
            }
            yield WorkSpaceDBManager.SetWorkSpaceName(wsFileName, newName);
            return true;
        });
    }
    clearAndSaveCurrentWorkSpace() {
        const cw = this.currentWorkspace;
        if (cw !== null) {
            cw.clear();
            void this.saveWorkSpace();
        }
    }
    clearCurrentWorkSpace() {
        const cw = this.currentWorkspace;
        if (cw !== null) {
            cw.clear();
        }
        this.currentWorkspace = null;
    }
    canClearCurrentWorkSpace() {
        const cw = this.currentWorkspace;
        return (cw === null || cw === void 0 ? void 0 : cw.getPanelsAmount()) === 0;
    }
    // -------------------------------- Shieeeeet ---------------------------------//
    disposeCurrentWorkspaceObj() {
        const ws = this.currentWorkspace;
        if (ws !== null)
            ws.dispose();
        this.currentWorkspace = null;
        this.workSpaceListChanged.Raise();
    }
    createCurrentWorkspaceObj(wsId, name) {
        this.currentWorkspace = new WorkSpace(name, wsId);
        this.workSpaceListChanged.Raise();
    }
    // -------------------------------- Shieeeeet ---------------------------------//
    // жопоболь: 91022 судя по всему ошибка возникает, когда после удаления единственного врк создаем врк с id Workspace1, но из базы врк еще не удалился -> пока не наведем порядок 84863 такой костылек
    getNewWorkSpaceFileName() {
        const wsList = this.wsList;
        const filenamePrefix = FILENAME_PREFIX;
        let key;
        do {
            key = filenamePrefix + (Math.floor(Math.random() * 100000) + 1);
        } while (wsList[key]);
        return key;
    }
    getDefaultWorkSpaceName() {
        const wsList = this.wsList;
        const defName = Resources.getResource('workspace.DefaultNameSpace');
        const keys = Object.keys(wsList);
        const dictToFind = {};
        let newDefName = '';
        for (let i = 0; i < keys.length; i++) {
            dictToFind[wsList[keys[i]]] = keys[i];
        }
        for (let i = 1; i <= MAX_WORKSPACE_COUNT; i++) {
            newDefName = defName + i;
            if (!dictToFind[newDefName])
                break;
        }
        return newDefName;
    }
    static workSpaceCompare(fileName1, fileName2) {
        const filenamePrefix = FILENAME_PREFIX;
        const number1 = parseInt(fileName1.substring(filenamePrefix.length));
        const number2 = parseInt(fileName2.substring(filenamePrefix.length));
        if (isNaN(number1) || isNaN(number2)) {
            return 0;
        }
        return number1 - number2;
    }
    getNewCurrentWs(preferredWsId = null) {
        const wsList = this.wsList;
        let newCurWsId;
        if (preferredWsId in wsList) {
            newCurWsId = preferredWsId;
        }
        else {
            // TODO. Ugly.
            const fileNames = Object.keys(wsList);
            fileNames.sort(_WorkSpaceManager.workSpaceCompare);
            newCurWsId = fileNames[fileNames.length - 1];
        }
        if (newCurWsId) {
            void this.changeWorkSpace(newCurWsId, false);
        }
        else {
            this.createWorkSpace();
        }
    }
    isWorkspaceLimitReached() {
        if (this.wsListLength() >= MAX_WORKSPACE_COUNT) {
            this.WorkspacesLimitReached.Raise();
            return true;
        }
        return false;
    }
    // -------------------------------- Shieeeeet ---------------------------------//
    wsListLength() {
        if (this.wsList === null) {
            return 0;
        }
        return Object.keys(this.wsList).length;
    }
    // TODO. Rename.
    addArrayToWsList(wsDataArray) {
        if (wsDataArray === null || wsDataArray.length === 0) {
            return;
        }
        const wsList = this.wsList;
        for (let i = 0, len = wsDataArray.length; i < len; i++) {
            const wsItem = wsDataArray[i];
            wsList[wsItem.ws_id] = wsItem.name;
        }
        this.workSpaceListChanged.Raise();
    }
    // TODO. Rename.
    addToWsList(wsId, name) {
        this.wsList[wsId] = name;
        this.workSpaceListChanged.Raise();
    }
    // TODO. Rename.
    deleteFromWsList(wsId) {
        if (isNullOrUndefined(wsId)) {
            return;
        }
        let changed = false;
        const wsList = this.wsList;
        if (wsId in wsList) {
            delete wsList[wsId];
            changed = true;
        }
        if (changed) {
            this.workSpaceListChanged.Raise();
        }
    }
    resetCurrentWithDefault() {
        // создает новый дефолтовый ws, затем удаляется старый активный ws
        const brokenWorkspaceID = this.currentWorkspace.fileName;
        const wsName = this.currentWorkspace.visibleName;
        UserWebStorageInstance.workspaces
            .getDefaultData()
            .then((defaultData) => {
            if (defaultData) {
                const newActiveID = defaultData.active_ws_id;
                if (newActiveID) {
                    this.changeWorkSpace(newActiveID, false, true)
                        .then(() => {
                        const cW = this.currentWorkspace;
                        if (cW) {
                            cW.visibleName = wsName;
                            this.addToWsList(cW.fileName, cW.visibleName);
                        }
                        this.removeWorkSpaces([brokenWorkspaceID]);
                        this.deleteFromWsList(brokenWorkspaceID);
                    })
                        .catch((e) => { });
                }
            }
        })
            .catch((e) => {
            this.getNewCurrentWs(); // на случай если дефолтового нет
        });
    }
    // -------------------------------- Shieeeeet ---------------------------------//
    toggleLock() {
        const workspace = this.currentWorkspace;
        if (workspace === null)
            return;
        workspace.locked = !workspace.locked;
        DockSystemInstance.dockManager.hideCloseButton(workspace.locked);
        DockSystemInstance.dockManager.resizeTabHost();
        void WorkSpaceDBManager.SetWorkSpaceLocked(workspace.fileName, workspace.locked);
        return workspace.locked;
    }
    processUndock() {
        if (this.currentWorkspace.locked) {
            MainWindowManager.MainWindow.Controls.popupBaloon.show(TerceraPopupBaloon.States.WorkspaceLocked);
            // Processed.
            return true;
        }
        return false;
    }
    processDockResize() {
        if (this.currentWorkspace.locked) {
            MainWindowManager.MainWindow.Controls.popupBaloon.show(TerceraPopupBaloon.States.WorkspaceLocked);
            // Processed.
            return true;
        }
        return false;
    }
    processDialogWindowDrag() {
        return this.currentWorkspace.locked;
    }
    isDirty(wsId) {
        const currentWorkSpace = this.currentWorkspace;
        return currentWorkSpace !== null && currentWorkSpace.fileName === wsId && currentWorkSpace.isDirty;
    }
    // -------------------------------- Shieeeeet ---------------------------------//
    startSaveLoop() {
        clearInterval(this.saveIntervalId);
        this.saveIntervalId = setInterval(() => {
            void this.saveWorkSpace();
        }, 60000);
        this.synchronizationIntervalId = setInterval(() => {
            void this.synchronizeWorkspaces();
        }, 600000);
    }
    stopSaveLoop() {
        clearInterval(this.saveIntervalId);
        clearInterval(this.synchronizationIntervalId);
        this.saveIntervalId = null;
        this.synchronizationIntervalId = null;
    }
    // -------------------------------- Shieeeeet ---------------------------------//
    updateSettings() {
        if (this.currentWorkspace !== null) {
            this.currentWorkspace.updateSettings();
        }
    }
    populateCurrentWorkspace() {
        if (this.currentWorkspace !== null) {
            this.currentWorkspace.populate();
        }
    }
    localize() {
        if (this.currentWorkspace !== null) {
            this.currentWorkspace.localize();
        }
    }
    themeChange() {
        if (this.currentWorkspace !== null) {
            this.currentWorkspace.themeChange();
        }
    }
    TickAsync() {
        if (this.currentWorkspace !== null) {
            this.currentWorkspace.TickAsync();
        }
    }
    RefreshPanelsWithInstrumentDescriptions(insArray) {
        if (this.currentWorkspace !== null) {
            this.currentWorkspace.RefreshPanelsWithInstrumentDescriptions(insArray);
        }
    }
    static numberOfPanelByType(panelType) {
        return WorkSpaceManager.currentWorkspace.numberOfPanelByType(panelType);
    }
    synchronizeWorkspaces() {
        return __awaiter(this, void 0, void 0, function* () {
            const workspaces = yield WorkSpaceDBManager.GetAllWorkSpaces();
            for (const ws of workspaces) {
                switch (ws.actionState) {
                    case DataBaseActions.ToRemove:
                        yield UserWebStorageInstance.workspaces.delete([ws.id]);
                        yield WorkSpaceDBManager.RemoveWorkSpace(ws.id);
                        break;
                    case DataBaseActions.Update:
                        this.processSaveWSOnServer(ws);
                        break;
                    case DataBaseActions.Add:
                        yield UserWebStorageInstance.workspaces.create(ws.id, { name: ws.data.name });
                        this.processSaveWSOnServer(ws);
                        break;
                }
            }
        });
    }
    processSaveWSOnServer(ws) {
        if (ws.isActive) {
            void UserWebStorageInstance.workspaces.setActiveWorkspaceId(ws.id);
        }
        if (ws.dataToSend.data) {
            void UserWebStorageInstance.workspaces.update(ws.id, this.prepareWSDataToSave(ws));
            void WorkSpaceDBManager.ClearWorkSpaceDataToSend(ws.id);
            if (wmh.isShowLogs()) {
                console.warn('Workspaces were saved to server');
            }
        }
        void WorkSpaceDBManager.SetDataBaseActionstate(ws.id, DataBaseActions.None);
    }
    prepareWSDataToSave(ws) {
        const result = ws.dataToSend.data || {};
        if (ws.hasOwnProperty('locked')) {
            result.locked = ws.locked;
        }
        if (ws.hasOwnProperty('name')) {
            result.name = ws.name;
        }
        if (ws.hasOwnProperty('updateTimeSpan')) {
            result.updateTimeSpan = ws.updateTimeSpan;
        }
        return result;
    }
    workspaceManagerHandlerInitialize() {
        workspaceManagerHandler.numberOfPanelByType = _WorkSpaceManager.numberOfPanelByType;
        workspaceManagerHandler.RefreshPanelsWithInstrumentDescriptions = (insArray) => {
            WorkSpaceManager.RefreshPanelsWithInstrumentDescriptions(insArray);
        };
    }
}
class LoadWorkSpaceResponse {
    constructor() {
        this.JsonData = null;
        this.IsLoaded = false;
        this.OnLoadedDelegate = null;
    }
    Clear() {
        this.JsonData = null;
        this.IsLoaded = false;
        this.OnLoadedDelegate = null;
    }
}
export const WorkSpaceManager = new _WorkSpaceManager();
window.WorkSpaceManager = WorkSpaceManager; // it is necessary to fix reference in dockspawn.js
